import {useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import type {UnknownAction} from 'redux'

type Callback = (response: any) => void

/**
 * A module-level parenting action creator: its own arguments, then the
 * `onSuccess` / `onFailure` callbacks (see setup/redux/Actions/Parenting).
 */
type ActionCreator = (...args: any[]) => UnknownAction

export type ParentingResource<T> = {
  data: T | null
  loading: boolean
  /** Server message of the last failure, if any. */
  error: string | null
  /** Server `code` of the last failure (e.g. PARENTING_CLASS_WAIT). */
  code: string | null
  /** Re-runs the request. */
  reload: () => void
}

type State<T> = Omit<ParentingResource<T>, 'reload'>

/**
 * Loads one parenting endpoint into component state and re-runs whenever
 * the arguments change.
 * @param creator stable, module-level action creator
 * @param args arguments passed before the callbacks (must be JSON-serialisable)
 * @param enabled set false to skip the request (e.g. a missing route param)
 * @returns the request state plus `reload`
 */
export const useParentingResource = <T>(
  creator: ActionCreator,
  args: ReadonlyArray<string | number | null | undefined | Record<string, unknown>> = [],
  enabled = true
): ParentingResource<T> => {
  const dispatch = useDispatch()
  const [reloadIndex, setReloadIndex] = useState(0)
  const [state, setState] = useState<State<T>>({
    data: null,
    loading: enabled,
    error: null,
    code: null,
  })
  const argsKey = JSON.stringify(args)

  useEffect(() => {
    if (!enabled) {
      setState({data: null, loading: false, error: null, code: null})
      return
    }
    let active = true
    setState((previous) => ({...previous, loading: true, error: null, code: null}))

    const onSuccess: Callback = (response) => {
      if (active)
        setState({data: response?.result ?? null, loading: false, error: null, code: null})
    }
    const onFailure: Callback = (response) => {
      if (active)
        setState({
          data: null,
          loading: false,
          error: response?.message || 'error',
          code: response?.code ?? null,
        })
    }

    dispatch(creator(...(JSON.parse(argsKey) as unknown[]), onSuccess, onFailure))
    return () => {
      active = false
    }
  }, [argsKey, creator, dispatch, enabled, reloadIndex])

  return {...state, reload: () => setReloadIndex((index) => index + 1)}
}
